import { create } from '@bufbuild/protobuf';

import {
  PairingOfferV1Schema,
  type PairingOfferV1,
} from '@mlo-local/protocol/generated/mlo/foundation/v1/foundation_pb.js';
import { encodePairingOffer } from '@mlo-local/protocol/qr.js';

import { MAX_OFFER_TTL_MS } from './foundation.js';

export type BrowserPairingOffer = {
  offer: PairingOfferV1;
  payload: string;
  manualCode: string;
  privateKey: CryptoKey;
};

export async function registerBrowserPairingOffer(input: {
  relayOrigin: string;
  result: BrowserPairingOffer;
  fetcher?: typeof fetch;
  allowInsecureLoopback?: boolean;
}): Promise<void> {
  const relayOrigin = validateOrigin(input.relayOrigin, 'https:', input.allowInsecureLoopback);
  const response = await (input.fetcher ?? fetch)(`${relayOrigin}/offers`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      code: input.result.manualCode,
      sessionId: sessionIdKey(input.result.offer.sessionId),
      payload: input.result.payload,
      expiresAtMs: Number(input.result.offer.expiresAtMs),
    }),
  });
  if (!response.ok) throw new Error('RELAY_OFFER_REJECTED');
}

/** Stable URL-safe relay key matching PhonePairingHost's session map key. */
export function sessionIdKey(value: Uint8Array): string {
  if (value.byteLength !== 16) throw new Error('INVALID_SESSION_ID');
  return [...value].map((part) => part.toString(16).padStart(2, '0')).join('');
}

export async function createBrowserPairingOffer(input: {
  connectorOrigin: string;
  signalingOrigin: string;
  browserLabel?: string;
  nowMs?: number;
  allowInsecureLoopback?: boolean;
}): Promise<BrowserPairingOffer> {
  const nowMs = input.nowMs ?? Date.now();
  const connectorOrigin = validateOrigin(
    input.connectorOrigin,
    'https:',
    input.allowInsecureLoopback,
  );
  const signalingOrigin = validateOrigin(
    input.signalingOrigin,
    'wss:',
    input.allowInsecureLoopback,
  );
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.subtle) throw new Error('WEB_CRYPTO_UNAVAILABLE');
  const keyPair = await cryptoApi.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits',
  ]);
  const browserPublicKey = new Uint8Array(
    await cryptoApi.subtle.exportKey('raw', keyPair.publicKey),
  );
  const sessionId = new Uint8Array(16);
  const nonce = new Uint8Array(32);
  cryptoApi.getRandomValues(sessionId);
  cryptoApi.getRandomValues(nonce);
  const offer = create(PairingOfferV1Schema, {
    protocolMajor: 1,
    sessionId,
    connectorOrigin,
    signalingOrigin,
    expiresAtMs: BigInt(nowMs + MAX_OFFER_TTL_MS),
    nonce,
    browserPublicKey,
    claimedBrowserLabel: sanitizeBrowserLabel(input.browserLabel),
  });
  return {
    offer,
    payload: encodePairingOffer(offer),
    manualCode: createManualCode(cryptoApi),
    privateKey: keyPair.privateKey,
  };
}

export function sanitizeBrowserLabel(value?: string): string {
  const normalized = (value ?? 'Desktop browser').normalize('NFKC').trim().replace(/\s+/gu, ' ');
  return (normalized || 'Desktop browser').slice(0, 100);
}

function validateOrigin(value: string, protocol: string, allowInsecureLoopback = false): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error('INVALID_ORIGIN');
  }
  const insecureProtocol = protocol === 'https:' ? 'http:' : 'ws:';
  const acceptedProtocol =
    url.protocol === protocol ||
    (allowInsecureLoopback &&
      url.protocol === insecureProtocol &&
      isLoopbackHostname(url.hostname));
  if (
    !acceptedProtocol ||
    url.username ||
    url.password ||
    url.pathname !== '/' ||
    url.search ||
    url.hash
  ) {
    throw new Error('INVALID_ORIGIN');
  }
  return url.origin.replace(/\/$/u, '');
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function createManualCode(cryptoApi: Crypto): string {
  const bytes = new Uint8Array(10);
  cryptoApi.getRandomValues(bytes);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  return Array.from(bytes, (value) => alphabet[value & 31]).join('');
}
